import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/common/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import TicketsPage from "@/pages/TicketsPage";
import TicketDetailPage from "@/pages/TicketDetailPage";
import ReportsPage from "@/pages/ReportsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ProfilePage from "@/pages/ProfilePage";
import UsersAdminPage from "@/pages/admin/UsersAdminPage";
import ApplicationsAdminPage from "@/pages/admin/ApplicationsAdminPage";
import ApplicationEditPage from "@/pages/admin/ApplicationEditPage";
import SLAAdminPage from "@/pages/admin/SLAAdminPage";
import DepartmentsAdminPage from "@/pages/admin/DepartmentsAdminPage";
import OrganizationsAdminPage from "@/pages/admin/OrganizationsAdminPage";
import SMTPAdminPage from "@/pages/admin/SMTPAdminPage";
import WhatsAppAdminPage from "@/pages/admin/WhatsAppAdminPage";
import AuditLogPage from "@/pages/AuditLogPage";
import RolesMatrixPage from "@/pages/admin/RolesMatrixPage";
import NotificationLogsPage from "@/pages/admin/NotificationLogsPage";
import { ROLES } from "@/lib/constants";
import { Toaster } from "@/components/ui/sonner";

const ADMIN_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.SUPPORT_ADMIN,
  ROLES.BUSINESS_ADMIN,
  ROLES.CLIENT_ADMIN,
];

function ThemedApp() {
  const { user } = useAuth();
  return (
    <ThemeProvider user={user}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Persistent authenticated application shell.
            Sidebar / Topbar remain mounted while only the
            route content changes through AppShell's Outlet. */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route
            path="/tickets"
            element={
              <ProtectedRoute permission="view_tickets">
                <TicketsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/:id"
            element={
              <ProtectedRoute permission="view_tickets">
                <TicketDetailPage />
              </ProtectedRoute>
            }
          />

          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/profile" element={<ProfilePage />} />

          <Route
            path="/admin/users"
            element={
              <ProtectedRoute roles={ADMIN_ROLES} permission="manage_users">
                <UsersAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/applications"
            element={
              <ProtectedRoute
                roles={[
                  ROLES.SUPER_ADMIN,
                  ROLES.SUPPORT_ADMIN,
                ]}
                permission="manage_applications"
              >
                <ApplicationsAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/applications/:appId"
            element={
              <ProtectedRoute
                roles={[
                  ROLES.SUPER_ADMIN,
                  ROLES.SUPPORT_ADMIN,
                ]}
                permission="manage_applications"
              >
                <ApplicationEditPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/sla"
            element={
              <ProtectedRoute
                roles={[
                  ROLES.SUPER_ADMIN,
                  ROLES.SUPPORT_ADMIN,
                ]}
                permission="manage_sla"
              >
                <SLAAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/organizations"
            element={
              <ProtectedRoute
                roles={[
                  ROLES.SUPER_ADMIN,
                  ROLES.SUPPORT_ADMIN,
                  ROLES.BUSINESS_ADMIN,
                ]}
              >
                <OrganizationsAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/departments"
            element={
              <ProtectedRoute
                roles={[
                  ROLES.SUPER_ADMIN,
                  ROLES.BUSINESS_ADMIN,
                ]}
              >
                <DepartmentsAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/smtp"
            element={
              <ProtectedRoute
                roles={[ROLES.SUPER_ADMIN]}
                permission="configure_smtp"
              >
                <SMTPAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/whatsapp"
            element={
              <ProtectedRoute
                roles={[ROLES.SUPER_ADMIN]}
                permission="configure_whatsapp"
              >
                <WhatsAppAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute
                roles={[
                  ROLES.SUPER_ADMIN,
                  ROLES.SUPPORT_ADMIN,
                ]}
                permission="view_audit"
              >
                <AuditLogPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute
                roles={[
                  ROLES.SUPER_ADMIN,
                  ROLES.SUPPORT_ADMIN,
                ]}
              >
                <RolesMatrixPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/notification-logs"
            element={
              <ProtectedRoute
                roles={[ROLES.SUPER_ADMIN]}
                permission="view_notification_logs"
              >
                <NotificationLogsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="*"
          element={<Navigate to="/dashboard" replace />}
        />
      </Routes>
    </ThemeProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemedApp />
          <Toaster
            position="top-right"
            richColors
            closeButton
            duration={4000}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
